import React, { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, UtensilsCrossed, Images, CalendarDays, ShoppingBag,
  Star, Settings as SettingsIcon, QrCode, LogOut, Home, BookOpen
} from "lucide-react";
import MenuAdmin from "./admin/MenuAdmin";
import GalleryAdmin from "./admin/GalleryAdmin";
import ReservationsAdmin from "./admin/ReservationsAdmin";
import OrdersAdmin from "./admin/OrdersAdmin";
import ReviewsAdmin from "./admin/ReviewsAdmin";
import SettingsAdmin from "./admin/SettingsAdmin";
import QRGeneratorAdmin from "./admin/QRGeneratorAdmin";
import DashboardOverview from "./admin/DashboardOverview";
import BlogAdmin from "./admin/BlogAdmin";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "menu", label: "Menu", icon: UtensilsCrossed },
  { id: "specials", label: "Chef's Specials", icon: Star },
  { id: "gallery", label: "Gallery", icon: Images },
  { id: "blog", label: "Blog / Stories", icon: BookOpen },
  { id: "reservations", label: "Reservations", icon: CalendarDays },
  { id: "orders", label: "QR Orders", icon: ShoppingBag },
  { id: "qr", label: "QR Generator", icon: QrCode },
  { id: "reviews", label: "Reviews", icon: Star },
  { id: "settings", label: "Home Page Edit", icon: SettingsIcon },
];

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("overview");

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#D4AF37]">Loading...</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const renderContent = () => {
    switch (tab) {
      case "overview": return <DashboardOverview onNavigate={setTab} />;
      case "menu": return <MenuAdmin specialsOnly={false} />;
      case "specials": return <MenuAdmin specialsOnly={true} />;
      case "gallery": return <GalleryAdmin />;
      case "blog": return <BlogAdmin />;
      case "reservations": return <ReservationsAdmin />;
      case "orders": return <OrdersAdmin />;
      case "qr": return <QRGeneratorAdmin />;
      case "reviews": return <ReviewsAdmin />;
      case "settings": return <SettingsAdmin />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#FDFBF7]" data-testid="admin-dashboard">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 min-h-screen border-r border-[#333] bg-[#0A0A0A] p-5 sticky top-0">
          <div className="mb-8">
            <h2 className="font-serif-display text-3xl text-gold-gradient">TULSI</h2>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#D1C7C9]/60 mt-1">Admin</p>
          </div>
          <nav className="space-y-1">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                data-testid={`admin-tab-${id}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  tab === id
                    ? "bg-gradient-to-r from-[#E69138]/20 to-[#D4AF37]/10 text-[#E69138] border border-[#E69138]/30"
                    : "text-[#D1C7C9] hover:bg-[#1A1A1A]"
                }`}
              >
                <Icon size={16} /> {label}
              </button>
            ))}
          </nav>
          <div className="mt-10 space-y-2">
            <Link to="/" className="flex items-center gap-3 px-3 py-2 text-xs uppercase tracking-widest text-[#D1C7C9]/70 hover:text-[#F2A65A]" data-testid="admin-view-site">
              <Home size={14} /> View Site
            </Link>
            <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2 text-xs uppercase tracking-widest text-[#D1C7C9]/70 hover:text-red-400" data-testid="admin-logout-btn">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 p-8">
          <div className="max-w-6xl mx-auto">{renderContent()}</div>
        </main>
      </div>
    </div>
  );
}
